import api from "./api";
import MedicalReportPromptType from "@/data/interfaces/MedicalReportPromptType";
import { Paginated } from "@/data/interfaces/PaginatedResponse";

export interface GetPromptTypesParams {
  per_page?: number;
  q?: string;
  is_active?: boolean;
  is_default?: boolean;
  with_prompts?: boolean;
}

export interface UploadRequest {
  type_id: number;
  file: File;
}

export interface UploadResponse {
  upload_id: string;
  filename: string;
  mime: string;
  size: number;
  expires_after: number;
  type_id: number;
  stage: string;
  conversation_id: string;
  file: {
    path: string;
    filename: string;
    mime: string;
  };
  results: Array<{
    key: string;
    prompt: string;
    answer: string;
    meta: any;
  }>;
}

export interface ClarifyRequest {
  upload_id: string;
  conversation_id: string;
  prompt_content: string;
  previous_answer: string;
}

export interface RunDefaultsRequest {
  upload_id: string;
  results: Array<{
    key: string;
    answer: string;
    clarify?: boolean;
  }>;
}

export interface ClarifyResponse {
  conversation_id: string;
  prompt: string;
  answer: string;
}

export interface RunDefaultsResponse {
  stage: string;
  base_results: Array<{
    key: string;
    answer: string;
    meta: any;
    clarify?: boolean;
  }>;
  refined: Array<{
    base: {
      key: string;
      answer: string;
    };
    conversation_id: string;
    file: {
      path: string;
      filename: string;
      mime: string;
    };
    by_default_prompts: Array<{
      key: string;
      answer: string;
      prompt: string;
      meta: any;
      base_from: string;
    }>;
  }>;
}

const medicalReportProtocol = {
  getPromptTypes: async (
    params?: GetPromptTypesParams
  ): Promise<Paginated<MedicalReportPromptType>> => {
    const resp = await api.get("/doctor/medical-report-types", {
      params,
    });
    return resp.data as Paginated<MedicalReportPromptType>;
  },

  upload: async (request: UploadRequest): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append("file", request.file);
    formData.append("type_id", request.type_id.toString());
    formData.append("max_tokens", "8192");

    const resp = await api.post("/medical-report-protocol/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return resp.data as UploadResponse;
  },

  clarify: async (request: ClarifyRequest): Promise<ClarifyResponse> => {
    const resp = await api.post("/medical-report-protocol/clarify", {
      upload_id: request.upload_id,
      conversation_id: request.conversation_id,
      prompt_content: request.prompt_content,
      previous_answer: request.previous_answer,
    });

    return resp.data as ClarifyResponse;
  },

  runDefaults: async (
    request: RunDefaultsRequest
  ): Promise<RunDefaultsResponse> => {
    const resp = await api.post("/medical-report-protocol/run-defaults", {
      upload_id: request.upload_id,
      results: request.results,
    });

    return resp.data as RunDefaultsResponse;
  },

  submit: async (payload: any) => {
    const resp = await api.post("/medical-report-protocol/submit", payload);
    return resp.data;
  },
};

export default medicalReportProtocol;
